import NavMenuItem from './NavMenuItem';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import routes from '@/routes/routes-enums';
import { FeatureFlags, UserRole } from '@repo/common-types';
import useMe from '@/services/auth/queries/useMe';
import { Environment, environment } from '@repo/environment';

type NavMenuItem = {
  isHeader?: boolean;
  id: string;
  label: string;
  icon?: string;
  relatedRoutes?: routes[];
} & (
  | { link: routes; subItems?: never }
  | { link?: never; subItems: NavMenuItem[] }
  | { link?: never; subItems?: never; isHeader: true }
);

const NavMenu = () => {
  const isAnalyticsBetaEnabled = useFeatureFlagEnabled(FeatureFlags.AnalyticsDashboard);
  const isAgentDealsEnabled = useFeatureFlagEnabled(FeatureFlags.CustomAgentDeals);
  const { data: meResponse } = useMe();
  const isSuperAdmin = meResponse?.role === UserRole.SUPER_ADMIN;

  const menuItems: NavMenuItem[] = [
    {
      id: 'menu',
      label: 'Menu',
      isHeader: true,
    },
    {
      id: 'anayltics',
      label: 'Analytics',
      icon: 'ri-dashboard-2-line',
      subItems: [
        {
          id: 'analyticsOverview',
          label: 'Overview',
          link: routes.ROOT,
        },
        // Conditionally include Beta Dashboard based on feature flag
        ...(isAnalyticsBetaEnabled
          ? [
              {
                id: 'analyticsBeta',
                label: 'Beta Dashboard',
                link: routes.ANALYTICS_BETA,
              },
            ]
          : []),
      ],
    },
    {
      id: 'tournaments',
      label: 'Tournaments',
      icon: 'ri-trophy-line',
      subItems: [
        {
          id: 'subTournaments',
          label: 'Tournaments',
          subItems: [
            {
              id: 'tournamentOverview',
              label: 'Overview',
              link: routes.TOURNAMENTS_OVERVIEW,
            },
            {
              id: 'tournamentList',
              label: 'Tournaments List',
              link: routes.TOURNAMENTS_LIST,
            },
            {
              id: 'tournamentTemplates',
              label: 'Templates',
              link: routes.TOURNAMENT_TEMPLATES,
            },
            {
              id: 'tournamentSchedules',
              label: 'Schedule',
              link: routes.TOURNAMENT_SCHEDULES,
            },
          ],
        },
        {
          id: 'sng',
          label: 'Sit & Go',
          subItems: [
            {
              id: 'sngOverview',
              label: 'Overview',
              link: routes.SNG_OVERVIEW,
            },
            {
              id: 'sngList',
              label: 'Sit & Go List',
              link: routes.SNG_LIST,
            },
            {
              id: 'sngTemplates',
              label: 'Templates',
              link: routes.SNG_TEMPLATES,
            },
          ],
        },
        {
          id: 'blindStructures',
          label: 'Blind Structures',
          link: routes.TOURNAMENT_BLIND_STRUCTURES,
        },
        {
          id: 'payoutStructures',
          label: 'Payout Structures',
          link: routes.TOURNAMENT_PAYOUT_STRUCTURES,
          relatedRoutes: [routes.TOURNAMENT_PAYOUT_STRUCTURE_DETAILS, routes.TOURNAMENT_PAYOUT_STRUCTURE_CREATE],
        },
        {
          id: 'tournamentSettings',
          label: 'Settings',
          link: routes.TOURNAMENT_SETTINGS,
        },
        {
          id: 'tournamentTickets',
          label: 'Tournament Tickets',
          link: routes.TOURNAMENT_TICKETS,
        },
      ],
    },
    {
      id: 'tables',
      label: 'Tables',
      icon: 'ri-apps-2-line',
      subItems: [
        {
          id: 'tableAnalytics',
          label: 'Overview',
          link: routes.TABLE_ANALYTICS,
        },
        {
          id: 'tableHands',
          label: 'Hand history',
          link: routes.TABLE_HANDS,
        },
        {
          id: 'tables',
          label: 'Tables',
          link: routes.TABLES,
        },
        {
          id: 'templates',
          label: 'Templates',
          link: routes.TEMPLATES,
        },
        {
          id: 'parametersRake',
          label: 'Blind Structures',
          link: routes.PARAMETERS_RAKE_STRUCTURE,
        },
        {
          id: 'parametersGameParams',
          label: 'Mixed Games',
          link: routes.PARAMETERS_MIXED_GAMES_STRUCTURE,
        },
        {
          id: 'tableSync',
          label: 'Table Sync',
          link: routes.TABLE_SYNC,
        },
        {
          id: 'contractSync',
          label: 'Contract Sync',
          link: routes.CONTRACT_SYNC,
        },
      ],
    },
    {
      id: 'users',
      label: 'Users',
      icon: 'ri-account-circle-line',
      subItems: [
        {
          id: 'userAnalytics',
          label: 'Overview',
          link: routes.USER_ANALYTICS,
        },
        {
          id: 'userList',
          label: 'Users',
          link: routes.USERS,
        },
        {
          id: 'propPlayers',
          label: 'Prop Players',
          link: routes.PROP_PLAYERS,
        },
        {
          id: 'agreemments',
          label: 'Agreements',
          link: routes.AGREEMENTS,
        },
        {
          id: 'deals',
          label: 'Deals',
          link: routes.USER_DEALS,
        },
      ],
    },
    ...(isAgentDealsEnabled
      ? [
          {
            id: 'agents',
            label: 'Agents',
            icon: 'ri-file-user-line',
            subItems: [
              // TODO: Add back when we develop the analytics page
              // {
              //   id: 'agentsOverview',
              //   label: 'Overview',
              //   link: routes.AGENTS_ANALYTICS,
              // },
              {
                id: 'agentsList',
                label: 'Agents',
                link: routes.AGENTS,
              },
              {
                id: 'agentsAgentDealUpdateRequests',
                label: 'Agent Deal Update Requests',
                link: routes.AGENTS_AGENT_DEAL_UPDATE_REQUESTS,
              },
              ...(isSuperAdmin
                ? [
                    {
                      id: 'agentsMigrations',
                      label: 'Migrations',
                      link: routes.AGENTS_MIGRATIONS,
                    },
                    {
                      id: 'agentsReferralDepthFix',
                      label: 'Referral Depth Fix',
                      link: routes.AGENTS_REFERRAL_DEPTH_FIX,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      id: 'reports',
      label: 'Reports',
      icon: 'ri-file-chart-line',
      subItems: [
        {
          id: 'signUpReport',
          label: 'Sign Up',
          link: routes.REPORTS_SIGNUP,
        },
      ],
    },
    {
      id: 'rake',
      label: 'Rake',
      icon: 'ri-money-dollar-circle-line',
      subItems: [
        {
          id: 'rakeList',
          label: 'Overview',
          link: routes.RAKE_ANALYTICS,
        },
        {
          id: 'rakeList',
          label: 'Table Rake',
          link: routes.RAKE_TABLE,
        },
        {
          id: 'rakeList',
          label: 'Player Rake',
          link: routes.RAKE_PLAYER,
        },
        {
          id: 'rakeList',
          label: 'Rake Share',
          link: routes.RAKE_SHARE,
        },
        {
          id: 'agreemments',
          label: 'Rake Back',
          link: routes.RAKE_BACK,
        },
        {
          id: 'agreemments',
          label: 'Rewards',
          link: routes.RAKE_REWARDS,
        },
      ],
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: 'ri-exchange-box-line',
      subItems: [
        {
          id: 'transactiosnList',
          label: 'Transactions',
          link: routes.TRANSACTIONS,
        },
      ],
    },
    {
      id: 'tokens',
      label: 'Tokens',
      icon: 'ri-nft-line',
      subItems: [
        {
          id: 'tokenFlowOrchestrator',
          label: 'Token Flow Wizard',
          link: routes.TOKEN_FLOW_ORCHESTRATOR_WIZARD,
        },
        {
          id: 'contractBalances',
          label: 'Contract Balances',
          link: routes.CONTRACT_BALANCES,
        },
      ],
    },
    {
      id: 'leaderboard',
      label: 'Leaderboard',
      icon: 'ri-medal-line',
      subItems: [
        {
          id: 'leaderboardConfig',
          label: 'Config',
          link: routes.LEADERBOARD_CONFIG,
        },
        {
          id: 'leaderboardPayouts',
          label: 'Payouts',
          link: routes.LEADERBOARD_PAYOUTS,
        },
        {
          id: 'leaderboardSchedule',
          label: 'Schedule',
          link: routes.LEADERBOARD_SCHEDULE,
        },
      ],
    },
    {
      id: 'deposits',
      label: 'Deposits',
      icon: 'ri-money-dollar-circle-line',
      link: routes.DEPOSITS,
    },
    {
      id: 'wallets',
      label: 'Wallets',
      icon: 'ri-wallet-2-line',
      subItems: [
        {
          id: 'walletTransfers',
          label: 'Wallet Transfers',
          link: routes.WALLET_TRANSFERS,
        },
        {
          id: 'bulkTransfers',
          label: 'Bulk Transfers',
          link: routes.BULK_TRANSFERS,
        },
      ],
    },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: 'ri-alarm-warning-line',
      link: routes.ALERTS,
    },
    ...(environment !== Environment.PRODUCTION
      ? [
          {
            id: 'devtools',
            label: 'Dev Tools',
            icon: 'ri-tools-line',
            link: routes.DEV_TOOLS,
          },
        ]
      : []),
  ];

  return (
    <ul className="navbar-nav" id="navbar-nav">
      {menuItems.map((item) => (
        <NavMenuItem key={item.id} item={item} />
      ))}
    </ul>
  );
};

export default NavMenu;
